import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext.jsx';
import { useOrganization } from '../../context/OrganizationContext';
import { useTheme } from '../../context/ThemeContext';
import {
  FiHome,
  FiFolder,
  FiCheckSquare,
  FiUsers,
  FiBarChart2,
  FiSettings,
  FiShield,
  FiTrendingUp,
  FiDatabase,
  FiShoppingBag,
  FiGrid,
  FiDollarSign,
  FiUserPlus,
  FiCreditCard,
  FiBriefcase,
  FiCalendar,
  FiMessageSquare
} from 'react-icons/fi';

/**
 * SIDEBAR STRUCTURE (Simplified - No Duplicates)
 * 
 * WORKSPACE (everyone):
 *   - Dashboard - Role-based content
 *   - Projects - Filtered by user access
 *   - Tasks - Assigned/accessible tasks
 *   - Teams - Teams user belongs to
 *   - Messages - Workspace conversations
 * 
 * INSIGHTS (managers+):
 *   - Analytics (dept_manager, org_admin)
 *   - Reports (dept_manager, org_admin)
 * 
 * APPS (everyone):
 *   - Finance - Budget & expense management
 *   - App Store - Integrations & apps
 * 
 * ADMINISTRATION (admins only):
 *   - Departments (dept_manager own, org_admin all)
 *   - Users (dept_manager own, org_admin all)
 *   - Billing (org_admin only)
 *   - Settings (org_admin only)
 * 
 * ADMIN PANELS (org_admin+):
 *   - Admin Panel (org_admin)
 *   - Platform Admin (platform_admin, super_admin)
 */

const ACCENT_STYLES = {
  blue: {
    activeBg: 'bg-blue-50 text-blue-700 border-blue-200',
    activeDark: 'dark:bg-blue-500/20 dark:text-blue-100 dark:border-blue-400',
    iconActive: 'text-blue-600 dark:text-blue-200'
  },
  teal: {
    activeBg: 'bg-teal-50 text-teal-700 border-teal-200',
    activeDark: 'dark:bg-teal-500/20 dark:text-teal-100 dark:border-teal-400',
    iconActive: 'text-teal-600 dark:text-teal-200'
  },
  purple: {
    activeBg: 'bg-purple-50 text-purple-700 border-purple-200',
    activeDark: 'dark:bg-purple-500/20 dark:text-purple-100 dark:border-purple-400',
    iconActive: 'text-purple-600 dark:text-purple-200'
  },
  amber: {
    activeBg: 'bg-amber-50 text-amber-700 border-amber-200',
    activeDark: 'dark:bg-amber-500/20 dark:text-amber-100 dark:border-amber-400',
    iconActive: 'text-amber-600 dark:text-amber-200'
  },
  indigo: {
    activeBg: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    activeDark: 'dark:bg-indigo-500/20 dark:text-indigo-100 dark:border-indigo-400',
    iconActive: 'text-indigo-600 dark:text-indigo-200'
  },
  slate: {
    activeBg: 'bg-slate-50 text-slate-700 border-slate-200',
    activeDark: 'dark:bg-slate-500/20 dark:text-slate-100 dark:border-slate-400',
    iconActive: 'text-slate-600 dark:text-slate-200'
  },
  rose: {
    activeBg: 'bg-rose-50 text-rose-700 border-rose-200',
    activeDark: 'dark:bg-rose-500/20 dark:text-rose-100 dark:border-rose-400',
    iconActive: 'text-rose-600 dark:text-rose-200'
  }
};

/**
 * Navigation Configuration
 * Each section has items with role and permission requirements
 * 
 * RBAC Rules:
 * - 'all' = everyone can see
 * - specific roles = only those roles can see
 * - permissions = checked via hasPermission() from OrganizationContext
 * - dynamic = item will be generated dynamically based on user data
 */
const SECTION_CONFIG = [
  // ─────────────────────────────────────────────────────────────
  // WORKSPACE - Core items everyone sees (flat structure)
  // ─────────────────────────────────────────────────────────────
  {
    key: 'workspace',
    title: 'Workspace',
    accent: 'blue',
    roles: ['all'],
    items: [
      {
        label: 'Dashboard',
        path: '/org/dashboard',
        icon: FiHome,
        permissions: [],
        roles: ['all'],
        description: 'Role-based overview with department/team info'
      },
      {
        label: 'Projects',
        path: '/org/projects',
        icon: FiFolder,
        permissions: ['canViewProjects'],
        roles: ['all'],
        description: 'All projects user has access to'
      },
      {
        label: 'Tasks',
        path: '/org/tasks',
        icon: FiCheckSquare,
        permissions: ['canViewTasks'],
        roles: ['all'],
        description: 'Assigned and accessible tasks'
      },
      {
        label: 'Timeline',
        path: '/org/tasks/timeline',
        icon: FiCalendar,
        permissions: [],
        roles: ['all'],
        description: 'Gantt chart view of all tasks'
      },
      {
        label: 'Teams',
        path: '/org/teams',
        icon: FiUsers,
        permissions: ['canViewTeams'],
        roles: ['all'],
        description: 'Teams user belongs to'
      },
      {
        label: 'Messages',
        path: '/org/messages',
        icon: FiMessageSquare,
        permissions: [],
        roles: ['all'],
        showUnread: true,
        description: 'Direct, project, and team chats'
      },
      // Dynamic "My Department" item - only for members and team leads
      // This will be replaced with actual department name and direct link
      {
        key: 'myDepartment',
        label: 'My Department', // Will be replaced dynamically
        path: '/org/departments', // Will be replaced with specific dept path
        icon: FiBriefcase,
        permissions: [],
        roles: ['member', 'team_lead'], // Only for non-admin roles
        dynamic: true, // Flag for dynamic handling
        description: 'Go to your department'
      }
    ]
  },

  // ─────────────────────────────────────────────────────────────
  // INSIGHTS - Analytics & Reporting (managers+)
  // ─────────────────────────────────────────────────────────────
  {
    key: 'insights',
    title: 'Insights',
    accent: 'purple',
    roles: ['team_lead', 'dept_manager', 'org_admin', 'platform_admin', 'super_admin'],
    items: [
      {
        label: 'Analytics',
        path: '/org/analytics',
        icon: FiBarChart2,
        permissions: ['canViewAnalytics'],
        roles: ['team_lead', 'dept_manager', 'org_admin', 'platform_admin'],
        description: 'Performance metrics and dashboards'
      },
      {
        label: 'Reports',
        path: '/org/reports',
        icon: FiTrendingUp,
        permissions: ['canViewAnalytics'],
        roles: ['dept_manager', 'org_admin', 'platform_admin'],
        description: 'Generated reports and exports'
      }
    ]
  },

  // ─────────────────────────────────────────────────────────────
  // APPS - Installed applications (everyone)
  // ─────────────────────────────────────────────────────────────
  {
    key: 'apps',
    title: 'Apps',
    accent: 'amber',
    roles: ['all'],
    items: [
      {
        label: 'Finance',
        path: '/org/finance',
        icon: FiDollarSign,
        permissions: [],
        roles: ['all'],
        description: 'Budget requests and expense management'
      },
      {
        label: 'App Store',
        path: '/org/app-store',
        icon: FiShoppingBag,
        permissions: [],
        roles: ['all'],
        description: 'Browse and install integrations'
      }
    ]
  },

  // ─────────────────────────────────────────────────────────────
  // ADMINISTRATION - Management (dept_manager+ for some, org_admin for all)
  // ─────────────────────────────────────────────────────────────
  {
    key: 'administration',
    title: 'Administration',
    accent: 'slate',
    roles: ['dept_manager', 'org_admin', 'platform_admin', 'super_admin'],
    items: [
      // Dynamic department link for dept_manager (shows their specific department)
      {
        key: 'myDeptAdmin',
        label: 'My Department', // Will be replaced dynamically with department name
        path: '/org/departments', // Will be replaced with specific dept path
        icon: FiGrid,
        permissions: [],
        roles: ['dept_manager'], // Only for dept managers
        dynamic: true,
        dynamicType: 'deptManagerDepartment',
        description: 'Manage your department'
      },
      // Departments list for org_admin and platform_admin (they manage all)
      {
        label: 'Departments',
        path: '/org/departments',
        icon: FiGrid,
        permissions: ['canManageDepartments'],
        roles: ['org_admin', 'platform_admin'],
        description: 'Manage all organization departments'
      },
      {
        label: 'Users',
        path: '/org/users',
        icon: FiUserPlus,
        permissions: ['canInviteUsers', 'canManageUsers'],
        roles: ['dept_manager', 'org_admin', 'platform_admin'],
        description: 'Invite and manage team members'
      },
      {
        label: 'Billing',
        path: '/org/billing',
        icon: FiCreditCard,
        permissions: ['canManageOrganization'],
        roles: ['org_admin'],
        description: 'Subscription and payment management'
      },
      {
        label: 'Settings',
        path: '/org/settings',
        icon: FiSettings,
        permissions: ['canManageOrganization'],
        roles: ['org_admin', 'platform_admin'],
        description: 'Organization settings and configuration'
      }
    ]
  },

  // ─────────────────────────────────────────────────────────────
  // ADMIN PANELS - High-sensitivity workspaces
  // ─────────────────────────────────────────────────────────────
  {
    key: 'adminPanels',
    title: 'Admin Panels',
    accent: 'indigo',
    roles: ['org_admin', 'platform_admin', 'super_admin'],
    items: [
      {
        label: 'Admin Panel',
        path: '/org/admin',
        icon: FiShield,
        permissions: ['canManageOrganization'],
        roles: ['org_admin'],
        description: 'Organization-wide governance'
      },
      {
        label: 'Platform Admin',
        path: '/platform/admin',
        icon: FiDatabase,
        permissions: [],
        roles: ['platform_admin', 'super_admin'],
        description: 'Platform-level administration'
      }
    ]
  }
];

const formatLabel = (value = '') =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const RoleBasedNavigation = ({ isMobile = false, isCollapsed = false, onItemClick }) => {
  const { currentUser, getUserRoles } = useAuth();
  const { hasPermission, departments } = useOrganization();
  const { isOrganizationThemed } = useTheme();
  const { totalUnread } = useChat();
  const location = useLocation();

  const userRolesObj = getUserRoles();
  const combinedRoles = [
    userRolesObj.systemRole,
    userRolesObj.platformRole,
    userRolesObj.orgRole,
    userRolesObj.role,
    currentUser?.departmentRole
  ].filter(Boolean);

  const uniqueRoles = Array.from(new Set(combinedRoles));
  const normalizedRoles = uniqueRoles.map((role) => role.toLowerCase());
  const userDepartments = currentUser?.departments || [];

  // Get user's department data for dynamic navigation
  const userDepartment = useMemo(() => {
    if (!departments || !currentUser?.departmentId) return null;
    const deptId = currentUser.departmentId?._id || currentUser.departmentId;
    return departments.find(d => (d._id || d.id) === deptId);
  }, [departments, currentUser?.departmentId]);

  // Check if user is an admin (dept_manager, org_admin, platform_admin, super_admin)
  const isAdmin = normalizedRoles.some(role => 
    ['dept_manager', 'org_admin', 'platform_admin', 'super_admin'].includes(role)
  );

  const sectionsToRender = useMemo(() => {
    // Check if user has ANY of the required roles
    const hasRoleAccess = (roles = ['all']) =>
      roles.includes('all') || roles.some((role) => normalizedRoles.includes(role));

    // Check if user has ANY of the required permissions
    const hasPermissionAccess = (permissions = []) =>
      permissions.length === 0 || permissions.some((permission) => hasPermission(permission));

    // Check if user is org_admin or higher (not just dept_manager)
    const isHigherAdmin = normalizedRoles.some(role => 
      ['org_admin', 'platform_admin', 'super_admin'].includes(role)
    );

    return SECTION_CONFIG
      .filter((section) => hasRoleAccess(section.roles))
      .map((section) => ({
        ...section,
        items: section.items
          .filter((item) => {
            // Skip dynamic myDepartment item for admins (they have full Departments link)
            if (item.dynamic && item.key === 'myDepartment' && isAdmin) {
              return false;
            }
            // Skip dynamic deptManagerDepartment for higher admins (they see all Departments)
            if (item.dynamic && item.dynamicType === 'deptManagerDepartment' && isHigherAdmin) {
              return false;
            }
            return hasRoleAccess(item.roles) && hasPermissionAccess(item.permissions);
          })
          .map((item) => {
            // Handle dynamic department item for members/team leads
            if (item.dynamic && item.key === 'myDepartment' && userDepartment) {
              return {
                ...item,
                label: userDepartment.name || 'My Department',
                path: `/org/departments/${userDepartment._id || userDepartment.id}`,
                description: `Go to ${userDepartment.name}`
              };
            }
            // Handle dynamic department item for dept_manager
            if (item.dynamic && item.dynamicType === 'deptManagerDepartment' && userDepartment) {
              return {
                ...item,
                label: userDepartment.name || 'My Department',
                path: `/org/departments/${userDepartment._id || userDepartment.id}`,
                description: `Manage ${userDepartment.name}`
              };
            }
            // If no department assigned, hide the dynamic item
            if (item.dynamic && !userDepartment) {
              return null;
            }
            return item;
          })
          .filter(Boolean) // Remove null items
      }))
      .filter((section) => section.items.length > 0);
  }, [normalizedRoles, hasPermission, isAdmin, userDepartment]);

  const isActiveRoute = (path) => location.pathname.startsWith(path);

  if (sectionsToRender.length === 0) {
    return (
      <div 
        className="px-4 py-8 text-center"
        style={isOrganizationThemed ? { color: 'var(--sidebar-text-muted)' } : { color: '#6B7280' }}
      >
        <FiShield className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No accessible navigation items</p>
      </div>
    );
  }

  const NavigationItem = ({ item, accent }) => {
    const isActive = isActiveRoute(item.path);
    const accentStyles = ACCENT_STYLES[accent] || ACCENT_STYLES.blue;
    const alignmentClass = isCollapsed ? 'justify-center' : (isMobile ? 'justify-start' : '');
    const paddingClass = isCollapsed ? 'p-2.5 w-12 h-12 mx-auto' : 'gap-2.5 px-2.5 py-1.5';

    // Use CSS variables for themed sidebar
    const themedStyles = isOrganizationThemed ? {
      color: isActive ? 'var(--sidebar-text)' : 'var(--sidebar-text-muted)',
      backgroundColor: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
      borderColor: isActive ? 'rgba(255,255,255,0.2)' : 'transparent'
    } : undefined;

    const themedHoverStyles = isOrganizationThemed ? {
      backgroundColor: 'var(--sidebar-hover-bg)',
      color: 'var(--sidebar-text)'
    } : undefined;

    return (
      <Link
        to={item.path}
        onClick={onItemClick}
        title={isCollapsed ? item.label : item.description}
        className={`group relative flex items-center rounded-lg text-[13px] font-medium border transition-colors ${
          isOrganizationThemed 
            ? '' 
            : isActive
              ? `${accentStyles.activeBg} ${accentStyles.activeDark || ''}`
              : 'text-gray-600 border-transparent hover:border-gray-200 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800/70 dark:hover:border-gray-700'
        } ${alignmentClass} ${paddingClass}`}
        style={themedStyles}
        onMouseEnter={(e) => {
          if (isOrganizationThemed && !isActive && themedHoverStyles) {
            e.currentTarget.style.backgroundColor = themedHoverStyles.backgroundColor;
            e.currentTarget.style.color = themedHoverStyles.color;
          }
        }}
        onMouseLeave={(e) => {
          if (isOrganizationThemed && !isActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--sidebar-text-muted)';
          }
        }}
      >
        <item.icon
          className={`w-4 h-4 ${
            isOrganizationThemed 
              ? '' 
              : isActive 
                ? accentStyles.iconActive 
                : 'text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-200'
          }`}
          style={isOrganizationThemed ? { color: 'inherit' } : undefined}
        />
        {!isCollapsed && <span>{item.label}</span>}
        {item.showUnread && totalUnread > 0 && (
          <span
            className={`${isCollapsed ? 'absolute -top-1 -right-1' : 'ml-auto'} inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white shadow-sm`}
          >
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </Link>
    );
  };

  return (
    <nav className={isCollapsed ? 'space-y-4 py-2 px-1 text-center' : 'space-y-4'}>
      {sectionsToRender.map((section, idx) => (
        <div key={section.key} className="space-y-1">
          {/* Section Title */}
          {!isCollapsed && (
            <div className={`px-3 ${idx > 0 ? 'mt-3' : ''}`}>
              <p 
                className={`text-[0.62rem] font-semibold tracking-[0.25em] uppercase ${
                  isOrganizationThemed ? '' : 'text-gray-400 dark:text-gray-500'
                }`}
                style={isOrganizationThemed ? { color: 'var(--sidebar-text-muted)', opacity: 0.8 } : undefined}
              >
                {section.title}
              </p>
            </div>
          )}
          
          {/* Section Items */}
          <div className={isCollapsed ? 'flex flex-col items-center gap-2' : 'space-y-0.5'}>
            {section.items.map((item) => (
              <NavigationItem key={item.key || item.path} item={item} accent={section.accent} />
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
};

export default RoleBasedNavigation;
